import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Icon from 'components/DesignSystem/Icon';
import EditButton from 'components/Shared/EditButton';
import RichHtml from 'components/RichHtml';
import AlertsCollection from 'components/AlertsCollection';
import EnlargeableImage from 'components/Meteorology/Fields/SynopticCharts/SynopticChart/Map/EnlargeableImage';
import LatestReportLink from 'components/Meteorology/Weather/Overview/LatestReportLink';
import mapAnnotations from 'helper/Meteorology/Weather/Overview/AnomalyPanel/mapAnnotations';
import { cssVar } from 'constants/theme';

import './index.scss';

export interface OverviewMobileProps {
  summary: string;
  image: string;
  error: boolean;
  period: string;
  setPeriod: React.Dispatch<React.SetStateAction<string>>;
  periodOptions: { value: string; label: string; }[];
  isAdmin: boolean;
  onEdit: () => void;
}

export default function OverviewMobile({
  summary,
  image,
  error,
  period,
  setPeriod,
  periodOptions,
  isAdmin,
  onEdit,
}: OverviewMobileProps) {
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const periodLabel = periodOptions.find((option) => option.value === period)?.label ?? '';

  const selectPeriod = (value: string) => {
    setPeriod(value);
    setPeriodSheetOpen(false);
  };

  return (
    <div className="oscillations-overview-mobile">
      <section className="ovm-card">
        <header className="ovm-card-header">
          <span className="ovm-card-title">Resumo das condições atuais</span>
          {isAdmin && <EditButton id="edit-summary" onClick={onEdit} />}
        </header>
        <RichHtml html={summary} className="ovm-summary ql-editor" />
        <div className="ovm-card-footer">
          <LatestReportLink />
        </div>
      </section>

      <section className="ovm-card">
        <header className="ovm-card-header">
          <span className="ovm-card-title">Painel de Oscilações · Anomalia da TSM</span>
        </header>
        <div className="ovm-map-body">
          <div className="ovm-map-row">
            <span className="ovm-map-label">Anomalia da TSM</span>
            <button
              type="button"
              className="ovm-period-trigger"
              onClick={() => setPeriodSheetOpen(true)}
            >
              <span>{periodLabel}</span>
              <Icon iconType="down_arrow" size="small" />
            </button>
          </div>

          {error || !image ? (
            <AlertsCollection
              alerts={[{ title: '', message: 'Imagem indisponível', variant: 'warning' }]}
            />
          ) : (
            <EnlargeableImage
              src={`data:image/png;base64,${image}`}
              className="ovm-map-image"
            />
          )}

          <div className="ovm-legend">
            {mapAnnotations.map((legend) => (
              <Link
                id={`legend-${legend.url}`}
                key={legend.acronym}
                to={`/meteorology/weather/oscillations/${legend.url}`}
                className="ovm-legend-item"
              >
                <span className="ovm-legend-sigla">{legend.acronym}</span>
                <span className="ovm-legend-name">{legend.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomSheet
        open={periodSheetOpen}
        dismiss={() => setPeriodSheetOpen(false)}
        title="Anomalia da TSM"
      >
        <ul className="ovm-period-list list-unstyled mb-0">
          {periodOptions.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className={`ovm-period-option${option.value === period ? ' active' : ''}`}
                onClick={() => selectPeriod(option.value)}
              >
                <span>{option.label}</span>
                {option.value === period && <Icon iconType="checkmark" color={cssVar('text-accent')} />}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>
    </div>
  );
}
